import { Customer } from "../shared/interfaces/Customer";
import { UserManageTableEntry } from "./interfaces";
import { UserRoles } from "./UserRolesEnum";
import React, { useContext, useState } from "react";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { CustomerDropdown } from "../CustomerDropdown";
import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { UserRolesDropdown } from "./UserRolesDropdown";
import { DataTable } from "primereact/datatable";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { UserManageHeaderToolbar } from "./UserManageTableHeaderToolbar";
import { ValidationMessage } from "../shared/ValidationMessage";
import { AuthContext } from "../contexts/AuthContext";
import { Region } from "../shared/interfaces/Region";
import { RegionType } from "../shared/enums/Region";

interface UserManageDetailTableProps {
  userManageTableEntries: UserManageTableEntry[];
  onUpdatedUserManageTableEntries: (userManageTableEntries: UserManageTableEntry[]) => void;
  onAddClaimClick: () => void;
}

const StyledDiv = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const StyledCompanyDropdown = styled(CompanyDropdown)`
  flex: 1;
`;

export function UserManageDetailTable(props: UserManageDetailTableProps) {
  const { t } = useTranslation();
  const { userManageTableEntries, onUpdatedUserManageTableEntries, onAddClaimClick } = props;

  const [singleCustomer, setSingleCustomer] = useState<boolean>(false);

  const authContext = useContext(AuthContext);

  function handleCustomerSelect(customer: Customer | undefined, rowId: string) {
    if (customer) {
      updateUserEntryCustomer(rowId, customer);
    }
  }

  const updateUserEntryCustomer = (rowId: string, customer: Customer): void => {
    const newUserManageTableEntries = userManageTableEntries.map((userManageTableEntry) => {
      if (userManageTableEntry.rowId === rowId) {
        return {
          ...userManageTableEntry,
          customer: customer,
          customerId: customer.id,
        };
      }
      return userManageTableEntry;
    });

    onUpdatedUserManageTableEntries(newUserManageTableEntries);
  };

  function handleSingleCustomer() {
    setSingleCustomer(true);
  }

  function handleCompanyChange(rowId: string, company: Region) {
    if (company) {
      updateUserEntryCompany(rowId, company);
    }
  }

  const updateUserEntryCompany = (rowId: string, company: Region): void => {
    const newUserManageTableEntries = userManageTableEntries.map((userManageTableEntry) => {
      if (userManageTableEntry.rowId === rowId) {
        return {
          ...userManageTableEntry,
          company: company,
        };
      }
      return userManageTableEntry;
    });
    onUpdatedUserManageTableEntries(newUserManageTableEntries);
  };

  function handleSiteChange(rowId: string, site: Region) {
    if (site) {
      updateUserEntrySite(rowId, site);
    }
  }

  function handleUserRoleSelect(userRole: UserRoles | undefined, rowId: string) {
    if (userRole) {
      updateUserEntryUserRole(rowId, userRole);
    }
  }

  const updateUserEntrySite = (rowId: string, site: Region): void => {
    const newUserManageTableEntries = userManageTableEntries.map((userManageTableEntry) => {
      if (userManageTableEntry.rowId === rowId) {
        return {
          ...userManageTableEntry,
          site: site,
        };
      }
      return userManageTableEntry;
    });

    onUpdatedUserManageTableEntries(newUserManageTableEntries);
  };

  const updateUserEntryUserRole = (rowId: string, userRole: UserRoles): void => {
    const newUserManageTableEntries = userManageTableEntries.map((userManageTableEntry) => {
      if (userManageTableEntry.rowId === rowId) {
        if (userRole === UserRoles.SuperAdmin) {
          return {
            ...userManageTableEntry,
            userRole: userRole,
            customer: undefined,
            company: undefined,
            site: undefined,
          };
        } else if (userRole === UserRoles.AdminUser) {
          return {
            ...userManageTableEntry,
            userRole: userRole,
            company: undefined,
            site: undefined,
          };
        } else {
          return {
            ...userManageTableEntry,
            userRole: userRole,
          };
        }
      }
      return userManageTableEntry;
    });

    onUpdatedUserManageTableEntries(newUserManageTableEntries);
  };

  function handleDelete(rowData: UserManageTableEntry) {
    const rowDeleted = userManageTableEntries.filter((entry) => entry.rowId !== rowData.rowId);
    onUpdatedUserManageTableEntries(rowDeleted);
  }

  function handleDeleteCompany(rowData: UserManageTableEntry) {
    const updatedEntries = userManageTableEntries.map((entry) =>
      entry.rowId === rowData.rowId ? { ...entry, company: undefined } : entry,
    );

    onUpdatedUserManageTableEntries(updatedEntries);
  }

  function handleDeleteSite(rowData: UserManageTableEntry) {
    const deletedSite = userManageTableEntries.map((entry) =>
      entry.rowId === rowData.rowId ? { ...entry, site: undefined } : entry,
    );

    onUpdatedUserManageTableEntries(deletedSite);
  }

  function handleAddClaimClick() {
    onAddClaimClick();
  }

  function checkDisabledFields(rowData: UserManageTableEntry, canIEditCustomer?: boolean) {
    return !canIEditCustomer || (rowData.userRole === UserRoles.SuperAdmin && !authContext.canI("add", "superAdmin"));
  }

  function checkDisabledFieldAdminUser(rowData: UserManageTableEntry, canIEditCustomer?: boolean) {
    return checkDisabledFields(rowData, canIEditCustomer) || rowData.userRole === UserRoles.AdminUser;
  }

  function checkDisabledFieldSuperAdmin(rowData: UserManageTableEntry, canIEditCustomer?: boolean) {
    if (rowData.userRole === UserRoles.SuperAdmin) {
      return true;
    } else {
      return rowData.customer && !canIEditCustomer;
    }
  }

  return (
    <DataTable
      header={<UserManageHeaderToolbar onAddClaimClick={handleAddClaimClick} />}
      value={userManageTableEntries}
      paginator
      rows={10}
    >
      <Column
        field="userRole"
        header={`${t("userManagement.userRole")} ${t("common.required")}`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          const canIEditCustomer = authContext.canI("edit", "customer", rowData.customer?.id);
          return (
            <>
              {rowData.userRole === UserRoles.SuperAdmin && !authContext.canI("add", "superAdmin") ? (
                <StyledDiv>{t("userManagement.superAdmin")}</StyledDiv>
              ) : (
                <>
                  <StyledDiv>
                    <UserRolesDropdown
                      onSelectUserRole={(userRole) => handleUserRoleSelect(userRole, rowData.rowId)}
                      selectedUserRole={rowData.userRole}
                      className={rowData.isUserRoleValid ? "" : "p-invalid"}
                      disabled={rowData.customer && rowData.userRole && checkDisabledFields(rowData, canIEditCustomer)}
                    />
                  </StyledDiv>
                  {!rowData.isUserRoleValid && (
                    <ValidationMessage
                      invalidMessage={t<string>("common.pleaseEnter", {
                        val: t("userManagement.userRole"),
                      })}
                      validationType="error"
                      showIcon
                    />
                  )}
                </>
              )}
            </>
          );
        }}
      />
      <Column
        field="customer"
        header={`${t("customer.customer")} ${t("common.required")}`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          const canIEditCustomer = authContext.canI("edit", "customer", rowData.customer?.id);
          return singleCustomer ? (
            <StyledDiv>{rowData?.customer?.name}</StyledDiv>
          ) : (
            <>
              <StyledDiv>
                <CustomerDropdown
                  onCustomerSelect={(customer) => handleCustomerSelect(customer, rowData.rowId)}
                  onSingleCustomerAvailable={handleSingleCustomer}
                  selectedCustomer={rowData.customer}
                  updateLocalStorage={false}
                  className={rowData.isCustomerValid ? "" : "p-invalid"}
                  disabled={rowData.userRole && checkDisabledFieldSuperAdmin(rowData, canIEditCustomer)}
                  filterUserManagement={true}
                />
              </StyledDiv>
              {!rowData.isCustomerValid && (
                <ValidationMessage
                  invalidMessage={t<string>("common.pleaseEnter", {
                    val: t("customer.customer"),
                  })}
                  validationType="error"
                  showIcon
                />
              )}
            </>
          );
        }}
      />
      <Column
        field="company"
        header={`${t("settings.subset")} 1`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          const canIEditCustomer = authContext.canI("edit", "customer", rowData.customer?.id);
          return (
            <StyledDiv>
              <StyledCompanyDropdown
                customerId={rowData.customer?.id}
                selectedCompanyName={rowData.company?.name ?? ""}
                onCompanyChange={(company, companyId) =>
                  handleCompanyChange(rowData.rowId, {
                    name: company,
                    id: companyId ?? 0,
                    regionType: RegionType.Company,
                    customerId: rowData.customer?.id,
                    geofenceId: 0,
                  })
                }
                disabled={
                  rowData.customer && rowData.userRole && checkDisabledFieldAdminUser(rowData, canIEditCustomer)
                }
                clearAuto
              />
              {checkDisabledFieldAdminUser(rowData, canIEditCustomer) ? null : (
                <Tooltip title={<div>{t("common.delete")}</div>}>
                  <span>
                    <IconButton onClick={() => handleDeleteCompany(rowData)} size="large">
                      <DeleteForeverIcon color={"error"} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </StyledDiv>
          );
        }}
      />
      <Column
        field="site"
        header={`${t("settings.subset")} 2`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          const canIEditCustomer = authContext.canI("edit", "customer", rowData.customer?.id);
          return (
            <StyledDiv>
              <SiteDropdown
                onSiteChange={(site, siteId) =>
                  handleSiteChange(rowData.rowId, {
                    name: site,
                    id: siteId ?? 0,
                    regionType: RegionType.Site,
                    customerId: rowData.customer?.id,
                    geofenceId: 0,
                  })
                }
                customerId={rowData.customer?.id}
                companyId={rowData.company?.id}
                selectedSiteName={rowData.site?.name ?? ""}
                disabled={
                  rowData.customer && rowData.userRole && checkDisabledFieldAdminUser(rowData, canIEditCustomer)
                }
                clearAuto
              />
              {rowData.customer && rowData.userRole && checkDisabledFieldAdminUser(rowData, canIEditCustomer) ? null : (
                <Tooltip title={<div>{t("common.delete")}</div>}>
                  <span>
                    <IconButton onClick={() => handleDeleteSite(rowData)} size="large">
                      <DeleteForeverIcon color={"error"} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </StyledDiv>
          );
        }}
      />
      <Column
        header={t("common.delete")}
        body={(rowData: UserManageTableEntry) => {
          const canIEditCustomer = authContext.canI("edit", "customer", rowData?.customer?.id);
          return (
            <>
              {rowData.customer && rowData.userRole && checkDisabledFields(rowData, canIEditCustomer) ? null : (
                <StyledDiv style={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip title={<div>{t("common.delete")}</div>}>
                    <span>
                      <IconButton onClick={() => handleDelete(rowData)} size="large">
                        <DeleteForeverIcon color={"error"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </StyledDiv>
              )}
              {rowData.isDuplicate && (
                <ValidationMessage
                  invalidMessage={t<string>("userManagement.duplicateRow")}
                  validationType="error"
                  showIcon
                />
              )}
            </>
          );
        }}
      />
    </DataTable>
  );
}
